import {
	Badge,
	Button,
	Tooltip,
	Fab,
	Card,
	TableCell,
	useTheme,
	Grid,
	useMediaQuery,
	Typography
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import AddIcon from '@mui/icons-material/Add';
import React, { useEffect, useRef, useState } from 'react';
import { Trans } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Blockquote } from '../../components/Blockquote';
import { LoadingScreen } from '../../components/LoadingScreen';
import { State } from '../reducers';
import { AcceptingDialog } from './AcceptingDialog';
import {
	openAddDialog,
	loadMoreProperties,
	applySearch,
	openAcceptAllDialog,
	loadProperties,
	loadPropertyPaymentResponsibilities,
	loadMorePropertyPaymentResponsibilities,
	_applySearch
} from './actions';
import { PropertyEditDialog } from './EditDialog';
import { RejectingDialog } from './RejectingDialog';
import { AddDialog } from './AddDialog';
import { AcceptAllDialog } from './AcceptAllDialog';
import { PropertiesTable } from './Table';
import { usePermission } from '../acl/permissions';
import { i18n } from '../../i18n';
import { Link } from 'react-router-dom';
import { useFeature, useAccountSpecificFeature } from '../acl/features';
import InfiniteScroll from 'react-infinite-scroll-component';
import { SearchBar as PropertySearchBar } from '../../components/SearchBar';
import { useTranslation } from 'react-i18next';
import { NoResultsForSearchScreen } from '../../components/NoResultsForSearchScreen';
import { InfiniteLoaderEndMessage } from '../../components/InfiniteLoaderEndMessage';
import { useIsFacilityManagerPlan } from '../../hooks/useIsFacilityManagerPlan';
import { useIsNewJanitor } from '../../hooks/useIsNewJanitor';
import { Filter } from '../../mui-custom/Filter';
import { AnchorEnum } from '../../mui-custom/Filter/filters.types';
import { useMobile } from '../../hooks/useBreakpoints';
import TableRowOrCard, { TableCtx } from '../../mui-custom/TableRowOrCard';
import { getProperties } from '../selectors';
import { useCheckViewport } from '../../hooks/useCheckViewport';
import clsx from 'clsx';
import { Check, Close, MonetizationOnOutlined } from '@mui/icons-material';
import {
	PropertyStatus,
	UIProperty,
	AccountSpecificFeature,
	Feature,
	OwnerExclusivePermission,
	ChargebeePlans
} from '@api/v1';
import { AcceptingPaymentResponsibilityDialog } from './AcceptingPaymentResponsibilityDialog';
import { RejectingPaymentResponsibilityDialog } from './RejectingPaymentResponsibilityDialog';

const useStyles = makeStyles((theme) => ({
	buttonBar: {
		display: 'flex',
		justifyContent: 'flex-end',
		margin: theme.spacing(1)
	},
	margin: {
		margin: theme.spacing(0, 4)
	},
	link: {
		textDecoration: 'none'
	},
	blockquote: {
		marginLeft: theme.spacing(2),
		marginRight: theme.spacing(2),
		width: 'auto',
		alignSelf: 'stretch',
		fontSize: '0.875rem'
	},
	fab: {
		position: 'sticky',
		left: '100%',
		bottom: theme.spacing(2),
		marginTop: theme.spacing(1),
		marginRight: theme.spacing(3),
		minHeight: '56px'
	},
	flex: {
		display: 'flex',
		flexDirection: 'column',
		height: '100%'
	},
	outer: {
		overflowY: 'hidden',
		overflowX: 'hidden',
		width: '100%',
		height: '100%'
	},
	outerTable: {
		overflowY: 'auto',
		width: '100%',
		padding: theme.spacing(1, 2)
	},
	outerSearchBar: {
		margin: theme.spacing(1, 1, 0),
		overflow: 'visible',
		zIndex: 1
	}
}));

export function PropertiesList(props: {}) {
	const isNewJanitor = useIsNewJanitor();
	const { activeSubscription } = useSelector((state: State) => state.admin.account);
	const plan = activeSubscription?.plan_id;
	const hasRelayFacilityManagerPlan = plan === ChargebeePlans.RELAY_FACILITY_MANAGER;

	if (typeof isNewJanitor === 'undefined') {
		return <LoadingScreen />;
	}
	if (isNewJanitor === false && !hasRelayFacilityManagerPlan) {
		return <DefaultPropertiesList {...props} />;
	} else if (isNewJanitor === true) {
		return <NewJanitorPropertiesTable {...props} />;
	}
}

const DefaultPropertiesList = (props: {}) => {
	const { data, maxAcceptedCount, currentAcceptedCount, hasMore, isFetching } = useSelector(
		(state: State) => ({
			data: state.properties?.list?.data,
			maxAcceptedCount: state.properties?.list?.metaData?.maxAcceptedCount,
			currentAcceptedCount: state.properties?.list?.metaData?.currentAcceptedCount,
			hasMore: state.properties?.list?.metaData?.hasMore,
			isFetching: state.properties?.list?.metaData?.isFetching
		})
	);
	const dispatch = useDispatch();
	useEffect(() => {
		dispatch(loadProperties());
	}, []);
	const loadMore = () => dispatch(loadMoreProperties());
	const classes = useStyles(props);
	const hasAccountView = usePermission(OwnerExclusivePermission.VIEW_ACCOUNT);
	const hasChargebeeCheckout = useFeature(Feature.ChargebeeCheckout);
	const [searchInput, setSearchInput] = useState('');
	const hasPricingModelV2 = useAccountSpecificFeature(AccountSpecificFeature.PricingModelV2);
	const isFacilityManagerPlan = useIsFacilityManagerPlan();
	const { t } = useTranslation();

	const UpgradeElement = hasAccountView ? (
		<Link to="/app/settings/subscription" className={classes.link}>
			<Button variant="contained" color="primary">
				{i18n.t('admin:licenses_upgrade_button')}
			</Button>
		</Link>
	) : (
		<Tooltip title={i18n.t('properties:contact_account_owner')}>
			<span>
				<Button variant="contained" color="primary" disabled>
					{i18n.t('admin:licenses_upgrade_button')}
				</Button>
			</span>
		</Tooltip>
	);
	return (
		<div className={classes.outer}>
			<div className={classes.flex}>
				<div className={classes.outerSearchBar}>
					{!hasPricingModelV2 && !isFacilityManagerPlan && (
						<Blockquote className={classes.blockquote}>
							<span>
								<Trans
									i18nKey="properties:licenses_blockquote"
									values={{
										maxAcceptedCount
									}}
								/>
								<Badge
									className={classes.margin}
									color={currentAcceptedCount >= maxAcceptedCount ? 'error' : 'primary'}
									badgeContent={`${currentAcceptedCount}/${
										maxAcceptedCount > 0 ? '*' : maxAcceptedCount
									}`}
								/>
							</span>
							{hasChargebeeCheckout && UpgradeElement}
						</Blockquote>
					)}
					<PropertySearchBar
						searchInput={searchInput}
						setSearchInput={setSearchInput}
						applySearch={applySearch}
						placeholder={'properties:search_placeholder'}
					/>
					<div className={classes.buttonBar}>
						<Button
							variant="contained"
							color="primary"
							onClick={() => dispatch(openAcceptAllDialog())}
						>
							<Trans i18nKey="properties:action_button_accept_all" />
						</Button>
					</div>
				</div>
				{data && data.length ? (
					<div className={classes.outerTable} id="properties-list-scroll-container">
						<InfiniteScroll
							next={loadMore}
							hasMore={hasMore}
							loader={<LoadingScreen />}
							dataLength={data.length}
							scrollableTarget="properties-list-scroll-container"
							endMessage={<InfiniteLoaderEndMessage itemName="properties" />}
						>
							<PropertiesTable />
							{isFetching && <LoadingScreen />}
							<AcceptingDialog />
							<AcceptAllDialog />
							<RejectingDialog />
							<PropertyEditDialog />
						</InfiniteScroll>
					</div>
				) : data ? (
					<NoResultsForSearchScreen
						message={
							searchInput
								? t('properties:nothing_found_on_search')
								: t('properties:nothing_available_for_this_account')
						}
					/>
				) : (
					<LoadingScreen />
				)}
				<AddDialog />
			</div>
			<Fab color="primary" onClick={() => dispatch(openAddDialog())} className={classes.fab}>
				<AddIcon />
			</Fab>
		</div>
	);
};

const _useStyles = makeStyles(({ spacing }: Theme) => ({
	wrapper: {
		width: '100%',
		height: '100%'
	},
	outer: {
		overflowY: 'hidden',
		display: 'flex',
		flexDirection: 'column',
		height: '100%'
	},
	gridContainer: {
		height: '100%',
		'@media print': {
			display: 'block'
		}
	},
	gridContainerWithStaticHeader: {
		height: `calc(100% - ${spacing(2)})`
	},
	fab: {
		position: 'fixed',
		right: spacing(2),
		bottom: spacing(2),
		marginTop: spacing(1),
		marginLeft: spacing(2),
		borderRadius: spacing(2),
		'@media print': {
			display: 'none'
		}
	},
	hideOnPrint: {
		'@media print': {
			display: 'none'
		}
	}
}));
const NewJanitorPropertiesTable = (props: {}) => {
	const { hasMore, isFetching } = useSelector((state: State) => ({
		hasMore: state.properties?.list?.metaData?.hasMore,
		isFetching: state.properties?.list?.metaData?.isFetching
	}));
	const { properties } = useSelector(getProperties);
	const dispatch = useDispatch();
	const classes = _useStyles(props);
	const isMobile = useMobile();
	const { t } = useTranslation();
	const theme = useTheme();
	const hasBigWidth = useMediaQuery(theme.breakpoints.up('md'));
	useEffect(() => {
		dispatch(loadPropertyPaymentResponsibilities());
	}, []);
	const [searchInput, setSearchInput] = useState('');

	const [delayTimer, setDelayTimer] = useState<NodeJS.Timer>();
	const handeSearchInput = (search) => {
		clearTimeout(delayTimer);
		setSearchInput(search.name);
		setDelayTimer(setTimeout(() => dispatch(_applySearch(search.name)), 500));
	};

	// for infinite scroll
	const loadMore = () => dispatch(loadMorePropertyPaymentResponsibilities());
	const lastElementRef = useRef<HTMLDivElement>(null);
	const isViewingLastElement = useCheckViewport(lastElementRef);
	useEffect(() => {
		if (isViewingLastElement) {
			loadMore();
		}
	}, [isViewingLastElement]);

	// accepting and rejecting dialogs
	const [acceptIsOpen, setAcceptIsOpen] = useState(false);
	const [rejectIsOpen, setRejectIsOpen] = useState(false);
	const [property, setProperty] = useState<UIProperty | null>(null);
	const closeAcceptingDialog = () => {
		setAcceptIsOpen(false);
		setProperty(null);
	};
	const closeRejectingDialog = () => {
		setRejectIsOpen(false);
		setProperty(null);
	};
	const headers = [
		t('properties:table_header_name'),
		t('properties:table_header_address'),
		t('properties:table_header_customer'),
		t('properties:table_header_status'),
		t('properties:table_header_cost_takeover'),
		''
	];

	return (
		<Grid
			container
			className={clsx([
				classes.gridContainer,
				{
					[classes.gridContainerWithStaticHeader]: hasBigWidth
				}
			])}
			sx={{ overflowY: 'auto' }}
		>
			<Grid item xs={12}>
				<div className={classes.wrapper}>
					{properties?.length === 0 && !isFetching && (
						<NoResultsForSearchScreen
							message={
								searchInput.length
									? t('properties:nothing_found_on_search')
									: t('properties:nothing_available_for_this_account')
							}
						/>
					)}
					{properties?.length > 0 && (
						<Card className={classes.outer} sx={{ m: isMobile ? 0 : 2 }}>
							<Filter
								side={AnchorEnum.RIGHT}
								onChange={handeSearchInput}
								textSearch={{
									filterKey: 'name',
									placeholder: t('properties:search_placeholder'),
									defaultValue: ''
								}}
								filters={[]}
								hasDrawerTrigger={false}
							/>
							<div style={{ width: '100%', overflowX: 'auto' }}>
								<TableCtx headers={headers}>
									{properties?.map((property) => (
										<>
											<TableRowOrCard
												key={property.id}
												sx={{
													'&:hover': { backgroundColor: theme?.palette?.secondaryShades4p }
												}}
											>
												<TableCell component="th" scope="row">
													{property.name}
												</TableCell>
												<TableCell component="th" scope="row">
													{property.address?.streetAddress ? (
														<>
															{property.address.streetAddress}, {property.address.postalCode}{' '}
															{property.address.locality}
														</>
													) : (
														<Typography
															component="span"
															sx={{ fontWeight: 600, fontSize: '1.1rem' }}
														>
															—
														</Typography>
													)}
												</TableCell>
												<TableCell component="th" scope="row">
													{property.customer?.name}
												</TableCell>

												<TableCell component="th" scope="row">
													{property.paymentResponsibility?.relayCostTakeoverAt && (
														<Trans
															i18nKey="properties:relay_cost_takeover_at"
															values={{
																date: Date.parse(
																	property.paymentResponsibility?.relayCostTakeoverAt as string
																)
															}}
														/>
													)}
													{property.paymentResponsibility?.casaviCostTakeoverApprovedAt && (
														<Trans
															i18nKey="properties:casavi_cost_takeover_at"
															values={{
																date: Date.parse(
																	property.paymentResponsibility
																		?.casaviCostTakeoverApprovedAt as string
																)
															}}
														/>
													)}
													{property.paymentResponsibility?.relayCostTakeoverRejectedAt && (
														<Trans
															i18nKey="properties:relay_cost_takeover_rejected_at"
															values={{
																date: Date.parse(
																	property.paymentResponsibility
																		?.relayCostTakeoverRejectedAt as string
																)
															}}
														/>
													)}
													{property.paymentResponsibility?.casaviCostTakeoverRejectedAt && (
														<Trans
															i18nKey="properties:casavi_cost_takeover_rejected_at"
															values={{
																date: Date.parse(
																	property.paymentResponsibility
																		?.casaviCostTakeoverRejectedAt as string
																)
															}}
														/>
													)}
													{!property.paymentResponsibility?.relayCostTakeoverAt &&
														!property.paymentResponsibility?.relayCostTakeoverRejectedAt &&
														!property.paymentResponsibility?.casaviCostTakeoverApprovedAt && (
															<Trans i18nKey="properties:status_pending_new" />
														)}
												</TableCell>
												<TableCell>
													<div
														style={{
															display: 'flex',
															alignItems: 'center'
														}}
													>
														<MonetizationOnOutlined
															style={{
																color: property.paymentResponsibility?.casaviCostTakeoverApprovedAt
																	? theme.palette.success.light
																	: theme.palette.error.light
															}}
														/>
														&nbsp;
														<Trans
															i18nKey={
																property.paymentResponsibility?.casaviCostTakeoverApprovedAt
																	? t('properties:tooltip_accept_paid')
																	: t('properties:tooltip_accept_unpaid')
															}
														/>
													</div>
												</TableCell>
												<TableCell>
													<Grid container columnSpacing={2} wrap="nowrap">
														<Grid item sx={{ 'min-width': '125px' }}>
															{property.status !== PropertyStatus.Rejected && (
																<Button
																	style={{ paddingLeft: 0 }}
																	color="error"
																	onClick={() => {
																		setRejectIsOpen(true);
																		setProperty(property);
																	}}
																>
																	<Close fontSize="small" style={{ marginRight: 4 }} />
																	<Trans i18nKey="properties:action_button_reject_new" />
																</Button>
															)}
														</Grid>
														<Grid item sx={{ 'min-width': '125px' }}>
															{property.status !== PropertyStatus.Accepted && (
																<Button
																	style={{ paddingLeft: 0 }}
																	color="primary"
																	onClick={() => {
																		setAcceptIsOpen(true);
																		setProperty(property);
																	}}
																>
																	<Check fontSize="small" style={{ marginRight: 4 }} />
																	<Trans i18nKey="properties:action_button_accept" />
																</Button>
															)}
														</Grid>
													</Grid>
												</TableCell>
											</TableRowOrCard>
											{hasMore && <div ref={lastElementRef}></div>}
										</>
									))}
								</TableCtx>
							</div>
							{isFetching && <LoadingScreen />}
							<PropertyEditDialog />
							<AcceptingPaymentResponsibilityDialog
								open={acceptIsOpen}
								property={property}
								closeAcceptingDialog={() => closeAcceptingDialog()}
							/>
							<RejectingPaymentResponsibilityDialog
								open={rejectIsOpen}
								property={property}
								closeRejectingDialog={() => closeRejectingDialog()}
							/>
						</Card>
					)}
				</div>
			</Grid>
		</Grid>
	);
};
